import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import MasterSelect from './MasterSelect';
import WorkerSelect from './WorkerSelect';
import PriceModeFields from './PriceModeFields';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

const EMPTY_HARVEST = {
  productName: '', fieldId: '', seasonId: '', harvestDate: new Date().toISOString().split('T')[0],
  quantity: '', unit: 'kg', qualityGrade: '1.sınıf', notes: '', masterProductId: '', plantingId: '',
};
const EMPTY_LABOUR = { workerName: '', numWorkers: 1, dailyRate: '', days: 1, paymentStatus: 'pending' };
const EMPTY_EXPENSE = { itemName: '', quantity: 1, unit: 'adet', unitPrice: '', paymentStatus: 'pending' };
const EMPTY_SALE = { contactName: '', quantitySold: '', unitPrice: '', totalAmount: '', priceMode: 'unit', paymentStatus: 'pending', saleDate: '' };

export default function HarvestFlow({ onClose, onSaved, prefill }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [harvest, setHarvest] = useState({ ...EMPTY_HARVEST, ...(prefill || {}) });
  const [labourList, setLabourList] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [saleList, setSaleList] = useState([]);
  const [fields, setFields] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [saving, setSaving] = useState(false);

  // Labour form state
  const [labourForm, setLabourForm] = useState(EMPTY_LABOUR);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE);
  const [saleForm, setSaleForm] = useState(EMPTY_SALE);

  useEffect(() => {
    api.get('/farm/fields').then(r => setFields(r.data.data || [])).catch(() => {});
    api.get('/seasons').then(r => {
      const data = r.data.data || [];
      setSeasons(data);
      const active = data.find(s => s.is_active);
      if (active && !harvest.seasonId) setHarvest(h => ({ ...h, seasonId: active.id }));
    }).catch(() => {});
  }, []); // eslint-disable-line

  const setH = (k, v) => setHarvest(h => ({ ...h, [k]: v }));

  const addLabour = () => {
    if (!labourForm.workerName) return;
    const total = (parseFloat(labourForm.dailyRate) || 0) * (parseFloat(labourForm.days) || 1) * (parseInt(labourForm.numWorkers) || 1);
    setLabourList(prev => [...prev, { ...labourForm, totalAmount: total }]);
    setLabourForm(EMPTY_LABOUR);
  };

  const addExpense = () => {
    if (!expenseForm.itemName) return;
    const total = (parseFloat(expenseForm.quantity) || 1) * (parseFloat(expenseForm.unitPrice) || 0);
    setExpenseList(prev => [...prev, { ...expenseForm, totalAmount: total }]);
    setExpenseForm(EMPTY_EXPENSE);
  };

  // Sale form setter that keeps quantitySold / unitPrice / totalAmount in sync
  const setSale = (k, v) => setSaleForm(f => {
    const next = { ...f, [k]: v };
    if (!['quantitySold', 'unitPrice', 'totalAmount', 'priceMode'].includes(k)) return next;
    const q = parseFloat(next.quantitySold);
    if (next.priceMode === 'total') {
      const total = parseFloat(next.totalAmount);
      next.unitPrice = (q > 0 && !isNaN(total)) ? String(total / q) : '';
    } else {
      const up = parseFloat(next.unitPrice);
      next.totalAmount = (q > 0 && !isNaN(up)) ? String(q * up) : '';
    }
    return next;
  });

  const addSale = () => {
    if (!saleForm.quantitySold || !saleForm.unitPrice) return;
    const total = (parseFloat(saleForm.quantitySold) || 0) * (parseFloat(saleForm.unitPrice) || 0);
    setSaleList(prev => [...prev, { ...saleForm, totalAmount: total, saleDate: saleForm.saleDate || harvest.harvestDate }]);
    setSaleForm(EMPTY_SALE);
  };

  const totalLabour = labourList.reduce((s, l) => s + (l.totalAmount || 0), 0);
  const totalExpense = expenseList.reduce((s, e) => s + (e.totalAmount || 0), 0);
  const totalSales = saleList.reduce((s, s2) => s + (s2.totalAmount || 0), 0);
  const totalSoldQty = saleList.reduce((s, s2) => s + (parseFloat(s2.quantitySold) || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/farm/harvests/unified', { harvest, labour: labourList, expenses: expenseList, sales: saleList });
      toast.success('Hasat kaydedildi!');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kayıt başarısız');
    } finally { setSaving(false); }
  };

  const dots = [1,2,3,4,5].map(i => (
    <span key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: step >= i ? 'var(--green-mid)' : '#ddd' }} />
  ));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflow: 'auto' }}>
        <div className="modal-handle" />
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>{dots}</div>

        {/* STEP 1 — Harvest info */}
        {step === 1 && (
          <div>
            <div className="modal-title">Hasat Bilgileri</div>
            <MasterSelect type="product" usageType="sell" label="Ürün *" value={harvest.productName}
              onChange={v => setH('productName', v)} onSelect={p => { setH('unit', p.unit || 'kg'); setH('masterProductId', p.id); }}
              placeholder="Buğday, Domates..." required priorityCategory="crop" />
            <div className="form-row">
              <div className="form-group">
                <label>Tarla</label>
                <select value={harvest.fieldId} onChange={e => setH('fieldId', e.target.value)}>
                  <option value="">Seçin</option>
                  {fields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Sezon</label>
                <select value={harvest.seasonId} onChange={e => setH('seasonId', e.target.value)}>
                  <option value="">Seçin</option>
                  {seasons.map(s => <option key={s.id} value={s.id}>{s.name}{s.is_active ? ' ●' : ''}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Miktar *</label>
                <input type="number" step="0.01" value={harvest.quantity} onChange={e => setH('quantity', e.target.value)} style={{ fontSize: '1.2rem', fontWeight: 700 }} required />
              </div>
              <div className="form-group">
                <label>Birim</label>
                <input value={harvest.unit} onChange={e => setH('unit', e.target.value)} readOnly style={{ background: '#f0f0f0' }} />
              </div>
            </div>
            <div className="form-group">
              <label>Kalite</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {['1.sınıf','2.sınıf','kırık'].map(q => (
                  <button key={q} type="button" onClick={() => setH('qualityGrade', q)}
                    style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: harvest.qualityGrade === q ? '2px solid var(--green-mid)' : '1px solid var(--border)',
                      background: harvest.qualityGrade === q ? 'var(--green-pale)' : '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    {q === '1.sınıf' ? '🥇 1. Sınıf' : q === '2.sınıf' ? '🥈 2. Sınıf' : '♻️ Düşük'}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Tarih</label>
              <input type="date" value={harvest.harvestDate} onChange={e => setH('harvestDate', e.target.value)} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
              onClick={() => setStep(2)} disabled={!harvest.productName || !harvest.quantity}>
              Devam →
            </button>
          </div>
        )}

        {/* STEP 2 — Labour */}
        {step === 2 && (
          <div>
            <div className="modal-title">İşçilik</div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>Hasat için kaç kişi çalıştı?</p>
            {labourList.map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 4, fontSize: '0.82rem' }}>
                <span>{l.workerName} · {l.numWorkers} kişi · {l.days} gün</span>
                <div><span style={{ fontWeight: 700 }}>{fmt(l.totalAmount)}</span>
                  <button onClick={() => setLabourList(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', marginLeft: 4 }}>✕</button>
                </div>
              </div>
            ))}
            <WorkerSelect
              label="İşçi / Grup Adı"
              value={labourForm.workerName}
              onChange={v => setLabourForm(f => ({ ...f, workerName: v }))}
              onWorkerSelect={w => setLabourForm(f => ({ ...f, workerName: `${w.first_name} ${w.last_name}`, dailyRate: w.default_daily_rate || f.dailyRate }))}
              placeholder="Ahmet, Sezonluk Ekip..."
            />
            <div className="form-row">
              <div className="form-group"><label>Kişi</label><input type="number" min="1" value={labourForm.numWorkers} onChange={e => setLabourForm(f => ({ ...f, numWorkers: e.target.value }))} /></div>
              <div className="form-group"><label>Gün</label><input type="number" step="0.5" value={labourForm.days} onChange={e => setLabourForm(f => ({ ...f, days: e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Günlük (₺)</label><input type="number" value={labourForm.dailyRate} onChange={e => setLabourForm(f => ({ ...f, dailyRate: e.target.value }))} /></div>
              <div className="form-group"><label>Ödeme</label>
                <select value={labourForm.paymentStatus} onChange={e => setLabourForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                  <option value="paid">Ödendi</option><option value="pending">Bekliyor</option>
                </select></div>
            </div>
            <button type="button" onClick={addLabour} style={{ width: '100%', padding: '8px', background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer', marginBottom: 8 }}>
              + İşçi Ekle
            </button>
            {totalLabour > 0 && <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Toplam İşçilik: {fmt(totalLabour)}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(1)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(3)}>Devam →</button>
            </div>
          </div>
        )}

        {/* STEP 3 — Expenses */}
        {step === 3 && (
          <div>
            <div className="modal-title">Diğer Giderler</div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: 8 }}>Naylon, kasa, taşıma, makine kiralama...</p>
            {expenseList.map((e, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 4, fontSize: '0.82rem' }}>
                <span>{e.itemName} × {e.quantity}</span>
                <div><span style={{ fontWeight: 700 }}>{fmt(e.totalAmount)}</span>
                  <button onClick={() => setExpenseList(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', marginLeft: 4 }}>✕</button>
                </div>
              </div>
            ))}
            <div className="form-group"><label>Gider Adı</label>
              <input value={expenseForm.itemName} onChange={e => setExpenseForm(f => ({ ...f, itemName: e.target.value }))} placeholder="Plastik Kasa, Taşıma..." /></div>
            <div className="form-row">
              <div className="form-group"><label>Miktar</label><input type="number" value={expenseForm.quantity} onChange={e => setExpenseForm(f => ({ ...f, quantity: e.target.value }))} /></div>
              <div className="form-group"><label>Fiyat (₺)</label><input type="number" value={expenseForm.unitPrice} onChange={e => setExpenseForm(f => ({ ...f, unitPrice: e.target.value }))} /></div>
            </div>
            <button type="button" onClick={addExpense} style={{ width: '100%', padding: '8px', background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer', marginBottom: 8 }}>
              + Gider Ekle
            </button>
            {totalExpense > 0 && <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Toplam Gider: {fmt(totalExpense)}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(2)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(4)}>Devam →</button>
            </div>
          </div>
        )}

        {/* STEP 4 — Sales */}
        {step === 4 && (
          <div>
            <div className="modal-title">Satış</div>
            <div style={{ background: '#f0f7ee', borderRadius: 8, padding: 10, marginBottom: 8, fontSize: '0.85rem' }}>
              Toplam hasat: <b>{harvest.quantity} {harvest.unit}</b> ·
              Satılan: <b>{totalSoldQty} {harvest.unit}</b> ·
              Kalan: <b style={{ color: parseFloat(harvest.quantity) - totalSoldQty > 0 ? '#2d5a27' : '#e74c3c' }}>{(parseFloat(harvest.quantity) - totalSoldQty).toFixed(1)} {harvest.unit}</b>
            </div>
            {saleList.map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f9f9f9', borderRadius: 8, marginBottom: 4, fontSize: '0.82rem' }}>
                <span>{s.contactName || 'Alıcı'} · {s.quantitySold} {harvest.unit}</span>
                <div><span style={{ fontWeight: 700, color: '#27ae60' }}>{fmt(s.totalAmount)}</span>
                  <button onClick={() => setSaleList(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', marginLeft: 4 }}>✕</button>
                </div>
              </div>
            ))}
            <MasterSelect
              type="contact"
              filter={{ type: 'buyer' }}
              label="Alıcı"
              value={saleForm.contactName}
              onChange={v => setSaleForm(f => ({ ...f, contactName: v }))}
              placeholder="Toptancı adı..."
            />
            <div className="form-group">
              <label>Miktar</label>
              <input type="number" value={saleForm.quantitySold} onChange={e => setSale('quantitySold', e.target.value)} placeholder={`Max: ${parseFloat(harvest.quantity) - totalSoldQty}`} />
            </div>
            <PriceModeFields
              mode={saleForm.priceMode} unit={harvest.unit}
              quantity={saleForm.quantitySold} unitPrice={saleForm.unitPrice} totalAmount={saleForm.totalAmount}
              onMode={m => setSale('priceMode', m)}
              onUnitPrice={v => setSale('unitPrice', v)}
              onTotal={v => setSale('totalAmount', v)}
            />
            <button type="button" onClick={addSale} style={{ width: '100%', padding: '8px', background: 'var(--green-pale)', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, color: 'var(--green-mid)', cursor: 'pointer', marginBottom: 8 }}>
              + Satış Ekle
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(3)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={() => setStep(5)}>Özete Git →</button>
            </div>
          </div>
        )}

        {/* STEP 5 — Summary */}
        {step === 5 && (
          <div>
            <div className="modal-title">Özet</div>
            <div className="card" style={{ padding: 12, marginBottom: 8, background: '#f0f7ee' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🌾 Hasat</div>
              <div style={{ fontSize: '0.85rem' }}>
                {harvest.productName} · {harvest.quantity} {harvest.unit} · {harvest.qualityGrade}
                {harvest.fieldId && ` · ${fields.find(f => f.id === harvest.fieldId)?.name || ''}`}
              </div>
            </div>
            {labourList.length > 0 && (
              <div className="card" style={{ padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>👷 İşçilik ({labourList.length} giriş)</div>
                {labourList.map((l, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{l.workerName} · {l.numWorkers}×{l.days} gün = {fmt(l.totalAmount)}</div>)}
                <div style={{ fontWeight: 700, marginTop: 4 }}>Toplam: {fmt(totalLabour)}</div>
              </div>
            )}
            {expenseList.length > 0 && (
              <div className="card" style={{ padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>🛒 Giderler ({expenseList.length} giriş)</div>
                {expenseList.map((e, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{e.itemName} × {e.quantity} = {fmt(e.totalAmount)}</div>)}
                <div style={{ fontWeight: 700, marginTop: 4 }}>Toplam: {fmt(totalExpense)}</div>
              </div>
            )}
            {saleList.length > 0 && (
              <div className="card" style={{ padding: 12, marginBottom: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>💰 Satış ({saleList.length} giriş)</div>
                {saleList.map((s, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{s.contactName || 'Alıcı'} · {s.quantitySold} {harvest.unit} × ₺{s.unitPrice} = {fmt(s.totalAmount)}</div>)}
                <div style={{ fontWeight: 700, marginTop: 4 }}>Toplam: {fmt(totalSales)}</div>
              </div>
            )}
            <div className="card" style={{ padding: 12, marginBottom: 12, background: totalSales - totalLabour - totalExpense >= 0 ? '#e8f5e4' : '#fde8e8' }}>
              <div style={{ fontWeight: 700 }}>📊 Net Sonuç</div>
              <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                <div>Gelir: {fmt(totalSales)}</div>
                <div>Gider: {fmt(totalLabour + totalExpense)}</div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', marginTop: 4, color: totalSales - totalLabour - totalExpense >= 0 ? '#2d5a27' : '#e74c3c' }}>
                  Net: {fmt(totalSales - totalLabour - totalExpense)}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }} onClick={() => setStep(4)}>← Geri</button>
              <button className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} onClick={handleSave} disabled={saving}>
                {saving ? 'Kaydediliyor...' : '✅ Tümünü Kaydet'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
